import { useState, useEffect } from "react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_API_URL || "/api";

interface User {
  username: string;
  blocked: boolean;
  status: string;
  error?: string;
}

export default function BedrockAccess() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{text: string, ok: boolean} | null>(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/bedrock-access/status`, { headers });
      const data = await res.json();
      setUsers(data.users || []);
    } catch {
      setMessage({ text: 'Failed to load status', ok: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleAction = async (username: string, action: 'block' | 'unblock') => {
    setActionLoading(username);
    setMessage(null);
    try {
      const res = await fetch(`${API}/bedrock-access/manage`, {
        method: 'POST', headers,
        body: JSON.stringify({ username, action })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ text: `${username} ${action === 'block' ? 'blocked' : 'unblocked'} successfully`, ok: true });
        await fetchStatus();
      } else {
        setMessage({ text: data.detail || 'Action failed', ok: false });
      }
    } catch {
      setMessage({ text: 'Request failed', ok: false });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '720px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#e2e8f0' }}>Bedrock Direct Access</h2>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Block or unblock direct AWS Bedrock access per user</p>
        </div>
        <button onClick={fetchStatus}
          style={{ fontSize: '12px', color: '#4f8ef7', background: 'transparent', border: '1px solid #1e3a5f', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer' }}>
          Refresh
        </button>
      </div>

      {message && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
            background: message.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${message.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: message.ok ? '#34d399' : '#f87171' }}>
          {message.text}
        </motion.div>
      )}

      {loading ? (
        <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {users.map(u => (
            <div key={u.username}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '10px', padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '12px', fontWeight: 600, color: '#fff',
                  background: u.blocked ? '#ef4444' : '#4f8ef7' }}>
                  {u.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: 500 }}>{u.username}</div>
                  <div style={{ fontSize: '11px', color: u.blocked ? '#f87171' : '#34d399', marginTop: '2px' }}>
                    {u.blocked ? 'Blocked — proxy only' : 'Direct access allowed'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {u.blocked ? (
                  <button onClick={() => handleAction(u.username, 'unblock')}
                    disabled={actionLoading === u.username}
                    style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer',
                      background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                      color: '#34d399', opacity: actionLoading === u.username ? 0.5 : 1 }}>
                    {actionLoading === u.username ? '...' : 'Unblock'}
                  </button>
                ) : (
                  <button onClick={() => handleAction(u.username, 'block')}
                    disabled={actionLoading === u.username}
                    style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer',
                      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                      color: '#f87171', opacity: actionLoading === u.username ? 0.5 : 1 }}>
                    {actionLoading === u.username ? '...' : 'Block'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
